import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus } from 'lucide-react';
import { useAssetRelationships } from '@/hooks/useAssetRelationships';
import { 
  RELATIONSHIP_TYPES, 
  IMPORTANCE_LEVELS, 
  AssetRelationshipFormData,
  RelationshipType,
  ImportanceLevel 
} from '@/types/asset-relationship';

const formSchema = z.object({
  target_asset_id: z.string().min(1, 'Selecione um ativo'),
  relationship_type: z.enum([
    'contains', 'depends_on', 'connects_to', 'hosts', 'uses', 
    'manages', 'monitors', 'backup_of', 'replaces', 'supports', 'part_of'
  ] as const),
  importance_level: z.enum(['critical', 'high', 'medium', 'low'] as const),
  description: z.string().optional(),
});

interface AddRelationshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceAssetId: string;
  sourceAssetName: string;
}

export const AddRelationshipDialog = ({
  open,
  onOpenChange,
  sourceAssetId,
  sourceAssetName,
}: AddRelationshipDialogProps) => {
  const { availableAssets, isLoadingAssets, createRelationship } = useAssetRelationships(sourceAssetId);

  const form = useForm<AssetRelationshipFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      target_asset_id: '',
      relationship_type: 'depends_on',
      importance_level: 'medium',
      description: '',
    },
  });

  const onSubmit = async (data: AssetRelationshipFormData) => {
    try {
      await createRelationship.mutateAsync(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao criar relacionamento:', error);
    }
  };

  const selectedRelationshipType = form.watch('relationship_type');
  const selectedImportanceLevel = form.watch('importance_level');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adicionar Relacionamento</DialogTitle>
          <DialogDescription>
            Crie um novo relacionamento para o ativo <strong>{sourceAssetName}</strong>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Seleção do ativo relacionado */}
            <FormField
              control={form.control}
              name="target_asset_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ativo Relacionado</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um ativo para relacionar" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingAssets ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span>Carregando ativos...</span>
                        </div>
                      ) : availableAssets.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          Nenhum ativo disponível para relacionamento
                        </div>
                      ) : (
                        availableAssets.map((asset) => (
                          <SelectItem key={asset.id} value={asset.id}>
                            <div className="flex items-center gap-2">
                              <span>{asset.nome}</span>
                              <Badge variant="outline" className="text-xs">
                                {asset.tipo_nome || 'Sem tipo'}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tipo de relacionamento */}
            <FormField
              control={form.control}
              name="relationship_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Relacionamento</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de relacionamento" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(RELATIONSHIP_TYPES).map(([key, type]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{type.label}</span>
                            <span className="text-xs text-muted-foreground">
                              {type.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRelationshipType && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {RELATIONSHIP_TYPES[selectedRelationshipType]?.description}
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Nível de importância */}
            <FormField
              control={form.control}
              name="importance_level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nível de Importância</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o nível de importância" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(IMPORTANCE_LEVELS).map(([key, level]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <div 
                              className={`w-3 h-3 rounded-full bg-${level.color}-500`}
                            />
                            <span>{level.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedImportanceLevel && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Prioridade: {IMPORTANCE_LEVELS[selectedImportanceLevel]?.priority}/4
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Descrição opcional */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva detalhes adicionais sobre este relacionamento..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={createRelationship.isPending}
              >
                {createRelationship.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Relacionamento
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
